// @ts-nocheck
import {
  CELL,
  CHUNK,
  MAX_ZOOM,
  MAX_ZOOM_STEP,
  ZOOM_LEVELS,
} from "./constants";
import { t } from "../i18n";
import {
  getGardenInitialZoom,
  isMobileViewport,
  syncVisualViewportInsets,
} from "../utils/viewport";

declare const lucide: { createIcons: () => void } | undefined;

function zoomUi() {
  return [
    { icon: "zoom-in", title: t("toolbar.zoomIn") },
    { icon: "zoom-in", title: t("toolbar.zoomInMore") },
    { icon: "zoom-out", title: t("toolbar.zoomReset") },
  ] as const;
}
import { mapState, mapRefs } from "./state";
import { genChunk, unloadChunk } from "./chunks";
import { fetchViewportIfNeeded } from "./data-sync";
import { clearGardenFocusLocation } from "../features/garden-location";

let viewportFetchTimer: ReturnType<typeof setTimeout> | null = null;
const CHUNK_FRAME_BUDGET_MS = 6;
const PAN_FETCH_IDLE_MS = 260;
const VIEWPORT_FALLBACK_SIZE = 800;
const MIN_CAMERA_Y = 0;

// Periodi della texture erba di #world::after (background-size in
// organisms/viewport.css) e origine storica del piano: il riposizionamento
// deve avvenire per multipli interi del periodo perché il pattern resti
// perfettamente continuo (nessun "salto" visivo).
const GRASS_TILE_W = 620;
const GRASS_TILE_H = 360;
const GRASS_BASE_LEFT = -12000;
const GRASS_BASE_TOP = -12000;

let grassLeft = GRASS_BASE_LEFT;
let grassTop = GRASS_BASE_TOP;
let grassSize = 24000;
let grassCamX = 0;
let grassCamY = 0;

const queuedChunks = new Set<string>();
let chunkDrainScheduled = false;
let chunkFetchAfterDrain = false;
let cachedViewportWidth = 0;
let cachedViewportHeight = 0;
let viewportMetricsListenerBound = false;

function scheduleViewportFetch(): void {
  if (mapState.cemeteryLoadState !== "ready") return;
  if (viewportFetchTimer) clearTimeout(viewportFetchTimer);
  viewportFetchTimer = setTimeout(() => {
    viewportFetchTimer = null;
    if (mapState.isPanning) {
      scheduleViewportFetch();
      return;
    }
    fetchViewportIfNeeded().catch((err) => {
      console.warn("Viewport fetch failed", err);
    });
  }, mapState.isPanning ? PAN_FETCH_IDLE_MS : 300);
}

/** Safari mostra/nasconde la barra URL senza sempre emettere "resize" sulla
 * window: solo il visualViewport riflette l'altezza visibile reale. */
function handleVisualViewportChange(): void {
  syncVisualViewportInsets();
  refreshViewportMetrics();
  update();
}

function refreshViewportMetrics(): void {
  const viewport = mapRefs.viewport;
  if (!viewport) return;
  if (!viewportMetricsListenerBound) {
    viewportMetricsListenerBound = true;
    window.addEventListener("resize", refreshViewportMetrics, { passive: true });
    window.addEventListener("orientationchange", refreshViewportMetrics, {
      passive: true,
    });
    window.visualViewport?.addEventListener(
      "resize",
      handleVisualViewportChange,
      { passive: true },
    );
  }
  cachedViewportWidth = viewport.offsetWidth || VIEWPORT_FALLBACK_SIZE;
  cachedViewportHeight = viewport.offsetHeight || VIEWPORT_FALLBACK_SIZE;
}

function getViewportMetrics() {
  if (!cachedViewportWidth || !cachedViewportHeight) refreshViewportMetrics();
  return {
    width: cachedViewportWidth || VIEWPORT_FALLBACK_SIZE,
    height: cachedViewportHeight || VIEWPORT_FALLBACK_SIZE,
  };
}

export function normalizeCameraBounds(): void {
  if (mapState.camY < MIN_CAMERA_Y) mapState.camY = MIN_CAMERA_Y;
}

/**
 * Ridimensiona/riposiziona il piano d'erba (#world::after) attorno alla
 * camera: ~3 schermate world invece di 24000×24000px fissi. Riposiziona solo
 * quando la camera si è spostata di più di mezza schermata dall'ultimo
 * ancoraggio (isteresi), con snap al periodo della texture.
 */
export function updateGrassTile(force = false): void {
  const world = mapRefs.world;
  if (!world) return;

  const { width: vw, height: vh } = getViewportMetrics();
  const span = Math.max(vw, vh) / mapState.zoomLevel;

  // Il repaint della pseudo è un piccolo hitch: durante il pan riancora solo
  // se stiamo per uscire dal piano (soglia piena), da fermi a mezza schermata.
  const threshold = mapState.isPanning ? span : span / 2;
  if (
    !force &&
    Math.abs(mapState.camX - grassCamX) < threshold &&
    Math.abs(mapState.camY - grassCamY) < threshold
  ) {
    return;
  }

  const size = Math.ceil((span * 4) / GRASS_TILE_W) * GRASS_TILE_W;
  const left =
    GRASS_BASE_LEFT +
    Math.round((mapState.camX - size / 2 - GRASS_BASE_LEFT) / GRASS_TILE_W) *
      GRASS_TILE_W;
  const top =
    GRASS_BASE_TOP +
    Math.round((mapState.camY - size / 2 - GRASS_BASE_TOP) / GRASS_TILE_H) *
      GRASS_TILE_H;

  grassCamX = mapState.camX;
  grassCamY = mapState.camY;

  if (left === grassLeft && top === grassTop && size === grassSize) return;
  grassLeft = left;
  grassTop = top;
  grassSize = size;
  world.style.setProperty("--grass-left", left + "px");
  world.style.setProperty("--grass-top", top + "px");
  world.style.setProperty("--grass-size", size + "px");
}

function getVisibleChunkRange() {
  const viewport = mapRefs.viewport;
  if (!viewport) return null;

  const { width: vw, height: vh } = getViewportMetrics();
  const vis = (Math.max(vw, vh) * 0.8) / mapState.zoomLevel;
  const x1 = mapState.camX - vis,
    x2 = mapState.camX + vis;
  const y1 = mapState.camY - vis,
    y2 = mapState.camY + vis;
  const chunkPx = CHUNK * CELL;
  return {
    minCX: Math.floor(x1 / chunkPx),
    maxCX: Math.floor(x2 / chunkPx),
    minCY: Math.floor(y1 / chunkPx),
    maxCY: Math.floor(y2 / chunkPx),
  };
}

function enqueueChunk(cx: number, cy: number): void {
  const key = cx + "," + cy;
  if (mapState.genChunks[key]) return;
  queuedChunks.add(key);
}

/** rIC con fallback per Safari/iOS (niente requestIdleCallback): setTimeout
 * come macrotask lascia al browser un paint tra uno slice e l'altro, mentre
 * rAF competerebbe con il frame di pan. */
export function scheduleIdle(run: () => void, timeout = 180): void {
  const ric = (window as any).requestIdleCallback;
  if (typeof ric === "function") {
    ric(run, { timeout });
    return;
  }

  setTimeout(run, 16);
}

function scheduleQueuedChunkDrain(): void {
  if (chunkDrainScheduled) return;
  chunkDrainScheduled = true;

  scheduleIdle(() => {
    chunkDrainScheduled = false;
    drainQueuedChunks();
  });
}

function drainQueuedChunks(): void {
  if (mapState.isPanning) {
    setTimeout(scheduleQueuedChunkDrain, PAN_FETCH_IDLE_MS);
    return;
  }

  // Center-out: genera prima i chunk più vicini alla camera, così il pop-in
  // parte da ciò che l'utente sta guardando.
  const chunkPx = CHUNK * CELL;
  const camCX = mapState.camX / chunkPx;
  const camCY = mapState.camY / chunkPx;
  const keys = Array.from(queuedChunks).sort((a, b) => {
    const [ax, ay] = a.split(",").map(Number);
    const [bx, by] = b.split(",").map(Number);
    return (
      Math.hypot(ax + 0.5 - camCX, ay + 0.5 - camCY) -
      Math.hypot(bx + 0.5 - camCX, by + 0.5 - camCY)
    );
  });

  // Chunk accodati da pan precedenti e ormai fuori vista: inutile generarli,
  // il prune li rimuoverebbe subito.
  const range = getVisibleChunkRange();

  const startedAt = performance.now();
  for (const key of keys) {
    queuedChunks.delete(key);
    const [cx, cy] = key.split(",").map(Number);
    if (
      range &&
      (cx < range.minCX - PRUNE_KEEP_MARGIN_CHUNKS ||
        cx > range.maxCX + PRUNE_KEEP_MARGIN_CHUNKS ||
        cy < range.minCY - PRUNE_KEEP_MARGIN_CHUNKS ||
        cy > range.maxCY + PRUNE_KEEP_MARGIN_CHUNKS)
    ) {
      continue;
    }
    genChunk(cx, cy);

    if (performance.now() - startedAt >= CHUNK_FRAME_BUDGET_MS) {
      scheduleQueuedChunkDrain();
      return;
    }
  }

  scheduleChunkPrune();

  if (chunkFetchAfterDrain) {
    chunkFetchAfterDrain = false;
    scheduleViewportFetch();
  }
}

const PRUNE_FRAME_BUDGET_MS = 5;
const PRUNE_KEEP_MARGIN_CHUNKS = 1;
let chunkPruneScheduled = false;

export function scheduleChunkPrune(): void {
  if (chunkPruneScheduled) return;
  chunkPruneScheduled = true;
  scheduleIdle(() => {
    chunkPruneScheduled = false;
    pruneOffscreenChunks();
  });
}

/**
 * Smonta i chunk fuori dal range visibile (+1 chunk di isteresi): senza
 * questo il DOM di #world cresce senza limite durante il pan ed è la causa
 * principale degli scatti. Idle-slice con budget: mai durante il pan.
 */
function pruneOffscreenChunks(): void {
  if (mapState.isPanning) {
    setTimeout(scheduleChunkPrune, PAN_FETCH_IDLE_MS);
    return;
  }

  const range = getVisibleChunkRange();
  if (!range) return;
  const minCX = range.minCX - PRUNE_KEEP_MARGIN_CHUNKS;
  const maxCX = range.maxCX + PRUNE_KEEP_MARGIN_CHUNKS;
  const minCY = range.minCY - PRUNE_KEEP_MARGIN_CHUNKS;
  const maxCY = range.maxCY + PRUNE_KEEP_MARGIN_CHUNKS;

  const startedAt = performance.now();
  for (const ck of Object.keys(mapState.chunkEls)) {
    const [cx, cy] = ck.split(",").map(Number);
    if (cx >= minCX && cx <= maxCX && cy >= minCY && cy <= maxCY) continue;
    unloadChunk(ck);

    if (performance.now() - startedAt >= PRUNE_FRAME_BUDGET_MS) {
      scheduleChunkPrune();
      return;
    }
  }
}

/** Lightweight camera update for active pan: world transform + grass tile. */
export function applyCameraTransform(
  opts: { forceGrass?: boolean } = {},
): void {
  const viewport = mapRefs.viewport;
  const world = mapRefs.world;
  if (!viewport || !world) return;

  normalizeCameraBounds();
  const ox = -mapState.camX;
  const oy = -mapState.camY;

  // scale first in string = translate applied first (right-to-left); matches legacy pan math.
  world.style.transform =
    "scale(" +
    mapState.zoomLevel +
    ") translate3d(" +
    ox +
    "px, " +
    oy +
    "px, 0)";

  // Check economico per frame: durante il pan la soglia piena (span intero in
  // updateGrassTile) riancora solo quando si sta per uscire dal piano d'erba.
  updateGrassTile(opts.forceGrass === true);
}

/** Generate visible procedural chunks and schedule API fetch (heavy work). */
export function ensureVisibleChunks(
  opts: { mode?: "sync" | "deferred"; scheduleFetch?: boolean } = {},
): void {
  normalizeCameraBounds();
  const range = getVisibleChunkRange();
  if (!range) return;

  const mode = opts.mode ?? "sync";
  const shouldScheduleFetch = opts.scheduleFetch ?? true;
  const { minCX, maxCX, minCY, maxCY } = range;

  if (mode === "deferred") {
    for (let cy = minCY; cy <= maxCY; cy++)
      for (let cx = minCX; cx <= maxCX; cx++) enqueueChunk(cx, cy);

    if (shouldScheduleFetch) chunkFetchAfterDrain = true;
    scheduleQueuedChunkDrain();
    return;
  }

  for (let cy = minCY; cy <= maxCY; cy++)
    for (let cx = minCX; cx <= maxCX; cx++) genChunk(cx, cy);

  if (shouldScheduleFetch) scheduleViewportFetch();
}

export function update(
  opts: { chunkMode?: "sync" | "deferred" } = {},
): void {
  refreshViewportMetrics();
  // Deferred di default: i burst sincroni di genChunk (zoom toggle, focus,
  // deep-link) bloccavano il main thread per decine/centinaia di ms.
  ensureVisibleChunks({ mode: opts.chunkMode ?? "deferred" });
  // Force: lo span dipende dallo zoom, quindi va ricalcolato anche da fermi.
  updateGrassTile(true);
  applyCameraTransform();
  scheduleChunkPrune();
}

export function resetGardenZoom(): void {
  mapState.zoomLevel = getGardenInitialZoom();
  mapState.zoomStep = 0;
  update();
  updateZoomButton();
}

export function goHome() {
  mapState.camX = 0;
  mapState.camY = 0;
  clearGardenFocusLocation();
  if (isMobileViewport()) resetGardenZoom();
  else update();
}

/** Center on a tomb; desktop uses sidebar max zoom, mobile keeps panoramic zoom. */
export function focusOnTomb(wx: number, wy: number): void {
  mapState.camX = wx;
  mapState.camY = wy;
  if (isMobileViewport()) {
    update();
    return;
  }
  mapState.zoomLevel = MAX_ZOOM;
  mapState.zoomStep = MAX_ZOOM_STEP;
  update();
  updateZoomButton();
}

export function updateZoomButton() {
  const zoomBtn = document.getElementById("zoom-btn");
  if (!zoomBtn) return;

  const step = mapState.zoomStep;
  zoomBtn.dataset.zoomStep = String(step);

  const ui = zoomUi()[step];
  zoomBtn.title = `${ui.title} (${step + 1}/${ZOOM_LEVELS.length})`;
  zoomBtn.setAttribute("aria-label", ui.title);

  const icon = zoomBtn.querySelector("i");
  if (icon) {
    icon.setAttribute("data-lucide", ui.icon);
    if (typeof lucide !== "undefined") lucide.createIcons();
  }
}

export function toggleZoom() {
  mapState.zoomStep = (mapState.zoomStep + 1) % ZOOM_LEVELS.length;
  mapState.zoomLevel = ZOOM_LEVELS[mapState.zoomStep];
  update();
  updateZoomButton();
}
